import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { useMemo, useState } from "react";
import { generateQuoteIndicationDraft } from "../../../api/routes";
import { SafeReportResponse } from "../Conversion";
import styles from "./QuoteIndicationModal.module.css";
import { InputNumber } from "primereact/inputnumber";
import { useToast } from "../../../context-providers/Toast";
import {
  DBPerilType,
  getEffectiveIncludedPerils,
  perilSentenceFromEnabledPerils,
} from "../general-input/util";
import { Dropdown } from "primereact/dropdown";
import {
  readableLayer,
  readableLimitOfLiabilityFromLayer,
  sortedLayersFromReport,
} from "../layers/util";
import * as client from "../../../client";
import { formatExactMoneyDollars, LayerPremiumSummary } from "../Money";
import { useCalculatedPremium } from "../data";

type QuoteIndicationModalProps = {
  specialtyPropertyInfo: SafeReportResponse;
  visible: boolean;
  setVisible: (visible: boolean) => void;
};

const getDeductibleEntries = (
  specialtyPropertyInfo: SafeReportResponse,
  includedPerils: DBPerilType[]
): client.DeductibleEntry[] => {
  const enabledFilename =
    specialtyPropertyInfo.report_json.policy_info?.selected_policy_form_name;
  const sortedPerils = [...includedPerils].sort();
  const deductibleInfo = specialtyPropertyInfo.report_json.deductible_info;
  if (!deductibleInfo) {
    return [];
  }

  const results: client.DeductibleEntry[] = [];
  const extracted = enabledFilename
    ? (deductibleInfo.extracted_deductibles[enabledFilename] ?? {})
    : {};

  for (const peril of sortedPerils) {
    // Manual overrides take precedence over extracted
    const deductible =
      deductibleInfo.manual_deductibles[peril] ?? extracted[peril];
    if (deductible) {
      const excerpts = {
        ...(deductible.direct_excerpts ?? {}),
        ...(deductible.manual_excerpt_overrides ?? {}),
      };
      for (const [name, description] of Object.entries(excerpts)) {
        results.push({ name, description });
      }
    }
  }

  return results;
};

export const QuoteIndicationModal: React.FC<QuoteIndicationModalProps> = ({
  specialtyPropertyInfo,
  visible,
  setVisible,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [premiumLow, setPremiumLow] = useState<number | null>(null);
  const [premiumHigh, setPremiumHigh] = useState<number | null>(null);
  const companyInfo = specialtyPropertyInfo.report_json.company_info;
  const sortedLayers = sortedLayersFromReport(specialtyPropertyInfo);
  const layerOptions = sortedLayers.map((l) => ({
    label: `${readableLayer(l)} (${l.layer_source})`,
    value: l.layer_id!,
  }));
  const [selectedLayerId, setSelectedLayerId] = useState(
    sortedLayers.find((layer) => layer.is_enabled)?.layer_id
  );
  const selectedLayer = sortedLayers.find(
    (layer) => layer.layer_id === selectedLayerId
  );
  const limitOfLiability = selectedLayer
    ? readableLimitOfLiabilityFromLayer(selectedLayer)
    : "";

  const { data: premiumData } = useCalculatedPremium(
    specialtyPropertyInfo.report_id,
    visible
  );

  const selectedLayerTotals = useMemo(() => {
    if (!premiumData || !selectedLayer?.layer_id) return null;
    const layerPremium = premiumData.layers.find(
      (l) => l.layer_id === selectedLayer.layer_id
    );
    if (!layerPremium) return null;
    return layerPremium.totals["resolved"] ?? null;
  }, [premiumData, selectedLayer]);

  const premiumRange =
    premiumLow != null && premiumHigh != null
      ? `${formatExactMoneyDollars(premiumLow, { withDollarSign: true, withRounding: true })} - ${formatExactMoneyDollars(premiumHigh, { withDollarSign: true, withRounding: true })}`
      : "";

  const effectivePerils = useMemo(() => {
    const allPerils =
      specialtyPropertyInfo.report_json.policy_info?.included_perils ?? [];
    if (!selectedLayer?.layer_id) return allPerils;
    return getEffectiveIncludedPerils(
      allPerils,
      premiumData,
      selectedLayer.layer_id
    );
  }, [specialtyPropertyInfo, selectedLayer, premiumData]);

  const perilsInsured = perilSentenceFromEnabledPerils(
    effectivePerils,
    specialtyPropertyInfo.report_json.policy_info?.peril_sentence_override
  );
  const deductibleEntries = getDeductibleEntries(
    specialtyPropertyInfo,
    effectivePerils
  );

  const toast = useToast();

  const handleCreateDraft = async () => {
    setIsLoading(true);

    try {
      const response = await generateQuoteIndicationDraft(
        specialtyPropertyInfo.report_id,
        perilsInsured,
        limitOfLiability,
        deductibleEntries,
        premiumRange
      );

      // Open the draft in a new tab
      window.open(response.draft_url, "_blank");
    } catch (error) {
      toast.show({
        severity: "error",
        summary: "Error Generating Draft Email",
        detail: "Something failed while generating the draft email.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      header="Quote Indication"
      visible={visible}
      style={{
        width: "700px",
      }}
      onHide={() => setVisible(false)}
      footer={
        <Button
          loading={isLoading}
          label="Create Draft"
          icon="pi pi-envelope"
          onClick={handleCreateDraft}
          disabled={!selectedLayer}
        />
      }
    >
      <div className={styles.verticalContainer}>
        <div className={styles.verticalContainerSmall}>
          <span className={styles.header}>Layer</span>
          <Dropdown
            value={selectedLayerId}
            options={layerOptions}
            onChange={(e) => setSelectedLayerId(e.value)}
            placeholder="Select a layer"
            style={{ width: "300px" }}
          />
        </div>
        <div className={styles.verticalContainerSmall}>
          <span className={styles.header}>Account Name</span>
          <span>{companyInfo.company_name}</span>
        </div>
        <div className={styles.verticalContainerSmall}>
          <span className={styles.header}>Effective Date</span>
          <span>{`${companyInfo.effective_date} - ${companyInfo.expiration_date}`}</span>
        </div>
        <div className={styles.verticalContainerSmall}>
          <span className={styles.header}>Perils Insured</span>
          <span>{perilsInsured}</span>
        </div>
        <div className={styles.verticalContainerSmall}>
          <span className={styles.header}>Limit of Liability</span>
          <span>{limitOfLiability}</span>
        </div>
        <div className={styles.verticalContainerSmall}>
          <span className={styles.header}>Total Insured Value</span>
          <span>
            {formatExactMoneyDollars(
              specialtyPropertyInfo.precomputed_metadata.total_tiv
            )}
          </span>
        </div>
        <div className={styles.verticalContainerSmall}>
          <span className={styles.header}>Deductibles</span>
          <div className={styles.verticalContainerSmall}>
            {deductibleEntries.map((entry, i) => (
              <div key={i}>
                <b style={{ fontSize: "14px" }}>{entry.name}:</b>{" "}
                {entry.description}
              </div>
            ))}
          </div>
        </div>
        <div className={styles.verticalContainerSmall}>
          <span className={styles.header}>Premium Range (Gross Layer)</span>
          {selectedLayerTotals && (
            <LayerPremiumSummary
              layerTotal={selectedLayerTotals.layer_total}
              shareTotal={selectedLayerTotals.share_total}
            />
          )}
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <InputNumber
              value={premiumLow}
              onValueChange={(e) => setPremiumLow(e.value ?? null)}
              mode="currency"
              currency="USD"
              locale="en-US"
              placeholder="Low"
            />
            <span>–</span>
            <InputNumber
              value={premiumHigh}
              onValueChange={(e) => setPremiumHigh(e.value ?? null)}
              mode="currency"
              currency="USD"
              locale="en-US"
              placeholder="High"
            />
          </div>
        </div>
      </div>
    </Dialog>
  );
};
